import { useState, useEffect } from 'react'
import type { Accomplishment, VocabularyTerm } from '../../stores/libraryStore'
import CompetencyTagPicker from './CompetencyTagPicker'

interface AccomplishmentFormProps {
  accomplishment?: Accomplishment | null
  competencyVocabulary: VocabularyTerm[]
  contextTagVocabulary: VocabularyTerm[]
  onSave: (data: FormData) => Promise<void>
  onCancel: () => void
  isEdit?: boolean
  submitLabel?: string
}

export interface FormData {
  employer: string
  role_title: string
  period: string
  description: string
  authority_notes: string
  role_context: string
  competency_tags: string[]
  context_tags: string[]
}

export default function AccomplishmentForm({
  accomplishment,
  competencyVocabulary,
  contextTagVocabulary,
  onSave,
  onCancel,
  isEdit = false,
  submitLabel,
}: AccomplishmentFormProps) {
  const [form, setForm] = useState<FormData>({
    employer: '',
    role_title: '',
    period: '',
    description: '',
    authority_notes: '',
    role_context: '',
    competency_tags: [],
    context_tags: [],
  })
  const [saving, setSaving] = useState(false)
  const [showVersionWarning, setShowVersionWarning] = useState(false)

  useEffect(() => {
    if (accomplishment) {
      setForm({
        employer: accomplishment.employer,
        role_title: accomplishment.role_title,
        period: accomplishment.period ?? '',
        description: '', // canonical_description not available from API
        authority_notes: accomplishment.authority_notes ?? '',
        role_context: accomplishment.role_context ?? '',
        competency_tags: accomplishment.competency_tags,
        context_tags: accomplishment.context_tags,
      })
    }
  }, [accomplishment])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      await onSave(form)
    } finally {
      setSaving(false)
    }
  }

  const handleDescriptionChange = (value: string) => {
    setForm((prev) => ({ ...prev, description: value }))
    if (isEdit && value.length > 0) {
      setShowVersionWarning(true)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 pt-16 px-4">
      <div className="w-full max-w-2xl rounded-lg bg-white shadow-xl max-h-[80vh] overflow-y-auto">
        <div className="border-b border-stone-100 px-6 py-4 flex items-center justify-between">
          <h2 className="font-semibold text-stone-800 text-sm">
            {isEdit ? 'Edit Proof' : 'Add Proof'}
          </h2>
          <button
            onClick={onCancel}
            className="text-stone-400 hover:text-stone-600 text-lg"
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1">
              Employer <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              required
              value={form.employer}
              onChange={(e) => setForm((prev) => ({ ...prev, employer: e.target.value }))}
              className="w-full rounded border border-stone-200 px-3 py-1.5 text-sm text-stone-800 focus:border-stone-400 focus:outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">
                Role Title <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                required
                value={form.role_title}
                onChange={(e) => setForm((prev) => ({ ...prev, role_title: e.target.value }))}
                className="w-full rounded border border-stone-200 px-3 py-1.5 text-sm text-stone-800 focus:border-stone-400 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">Period</label>
              <input
                type="text"
                value={form.period}
                onChange={(e) => setForm((prev) => ({ ...prev, period: e.target.value }))}
                placeholder="e.g. 2023–present"
                className="w-full rounded border border-stone-200 px-3 py-1.5 text-sm text-stone-800 placeholder:text-stone-400 focus:border-stone-400 focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1">
              Description {!isEdit && <span className="text-red-400">*</span>}
            </label>
            {showVersionWarning && (
              <p className="text-xs text-amber-600 mb-1">
                Editing the description creates a new version. Existing perspectives may need review.
              </p>
            )}
            <textarea
              required={!isEdit}
              value={form.description}
              onChange={(e) => handleDescriptionChange(e.target.value)}
              rows={4}
              placeholder={isEdit ? 'Leave empty to keep current description' : 'Describe the accomplishment...'}
              className="w-full rounded border border-stone-200 px-3 py-1.5 text-sm text-stone-800 placeholder:text-stone-400 focus:border-stone-400 focus:outline-none resize-y"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1">
              Authority Notes
            </label>
            <textarea
              value={form.authority_notes}
              onChange={(e) => setForm((prev) => ({ ...prev, authority_notes: e.target.value }))}
              rows={2}
              placeholder="Your actual role, initiative level, decision scope..."
              className="w-full rounded border border-stone-200 px-3 py-1.5 text-sm text-stone-800 placeholder:text-stone-400 focus:border-stone-400 focus:outline-none resize-y"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1">
              Role Context
            </label>
            <textarea
              value={form.role_context}
              onChange={(e) => setForm((prev) => ({ ...prev, role_context: e.target.value }))}
              rows={2}
              placeholder="Scale, team size, industry context..."
              className="w-full rounded border border-stone-200 px-3 py-1.5 text-sm text-stone-800 placeholder:text-stone-400 focus:border-stone-400 focus:outline-none resize-y"
            />
          </div>

          <CompetencyTagPicker
            selectedTags={form.competency_tags}
            vocabulary={competencyVocabulary}
            onChange={(tags) => setForm((prev) => ({ ...prev, competency_tags: tags }))}
          />

          <CompetencyTagPicker
            selectedTags={form.context_tags}
            vocabulary={contextTagVocabulary}
            onChange={(tags) => setForm((prev) => ({ ...prev, context_tags: tags }))}
            label="Context Tags"
          />

          <div className="border-t border-stone-100 pt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="rounded px-3 py-1.5 text-xs text-stone-600 hover:bg-stone-50 border border-stone-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded px-3 py-1.5 text-xs text-white bg-stone-800 hover:bg-stone-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : submitLabel ?? (isEdit ? 'Save Changes' : 'Add Proof')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
